/**
 * 底栏 / FAB / Whisper 主麦克风 — 几何、对齐与可调外观
 *
 * Layout 与 MicButton 共用本文件，使底部导航上展开的 FAB「Tap to talk」圆钮中心
 * 与 Whisper 页主麦克风圆钮中心在视口上重合（同一套 bottom / 面板高度推导）。
 *
 * 整钮直径：`FAB_MAIN_CIRCLE_DIAMETER_PX`；圆内图标：`FAB_MAIN_CIRCLE_ICON_PX`（二者独立）。
 * 若仅改 Whisper 主麦位置：调 `WHISPER_MIC_*`；`FAB_MENU_TOP_BUTTON_TOP_OFFSET_REM` 会随之重算。
 * 若改 FAB 面板高度或底栏高度：公式会自动对齐；若改「Tap to talk」文案行高，请同步
 * `FAB_TAP_TO_TALK_LABEL_HEIGHT_PX` / `FAB_TAP_TO_TALK_LABEL_GAP_PX`（与 Layout 中 gap、字号一致）。
 */

/** 假定根字体 1rem = 16px（与 Tailwind 默认一致） */
const ROOT_FONT_PX = 16;

// =============================================================================
// ① 底栏凹槽与高度（与 Layout clipPath 一致）
// =============================================================================

/**
 * 半圆「直径」= 2 × BUMP_R_PX（只改半径即可放大/缩小凹槽）。
 * 约束：BUMP_TOP_PX ≥ BUMP_R_PX；TAB_BAR_H_PX = BUMP_TOP_PX + NAV_ICONS_H_PX（底部图标行 h-20）。
 * 圆心在平顶线上 (0.5, yFlat)；中间「+」按钮中心与该线对齐。
 */
export const NAV_ICONS_H_PX = 80;
export const BUMP_R_PX = 36;
export const BUMP_TOP_PX = 36;
export const TAB_BAR_H_PX = BUMP_TOP_PX + NAV_ICONS_H_PX;
const W_REF_PX = 400;
const yFlat = BUMP_TOP_PX / TAB_BAR_H_PX;
const rx = BUMP_R_PX / W_REF_PX;
const ry = BUMP_R_PX / TAB_BAR_H_PX;

export const tabBarClipPathD = `M0,1 L1,1 L1,${yFlat} L${0.5 + rx},${yFlat} A ${rx} ${ry} 0 0 0 ${0.5 - rx},${yFlat} L0,${yFlat} L0,1 Z`;
/** 仅顶边：水平段 + 上半圆弧（与 clip 弧反向，从左到右） */
export const tabBarTopEdgeD = `M0,${yFlat} L${0.5 - rx},${yFlat} A${rx},${ry} 0 0 1 ${0.5 + rx},${yFlat} L1,${yFlat}`;

// =============================================================================
// ② 凹槽顶边描边（SVG）
// =============================================================================

export const TAB_BAR_TOP_EDGE_STROKE_LIGHT = 'rgba(255,255,255,0.72)';
export const TAB_BAR_TOP_EDGE_STROKE_DARK = 'rgba(255,255,255,0.42)';
export const TAB_BAR_TOP_EDGE_STROKE_WIDTH = 1.25;

// =============================================================================
// ③ 中间「+」按钮（相对底栏容器）
// =============================================================================

export const FAB_CENTER_BUTTON_BOTTOM_REM = 3.25;
export const FAB_CENTER_BUTTON_SIZE_REM = 3.5;
export const FAB_CENTER_BUTTON_BG = '#FFFBAA';
export const FAB_CENTER_BUTTON_TEXT = '#193182';

// =============================================================================
// ④ FAB 展开面板 + 与 Whisper 主麦对齐
// =============================================================================

export const FAB_MENU_PANEL_GAP_BELOW_REM = 0.5;
export const FAB_MENU_PANEL_HEIGHT_REM = 14;
export const FAB_MENU_PANEL_MAX_WIDTH_REM = 13.5;

/** Check-in / Type Chat 相对水平中线左右偏移（rem） */
export const FAB_RADIAL_SIDE_OFFSET_REM = 6;

/** 底部两钮相对展开面板底边（rem） */
export const FAB_MENU_BOTTOM_ROW_BOTTOM_OFFSET_REM = -1;

// --- Whisper 主麦 ↔ FAB「Tap to talk」：同一圆直径（只改一处即可两边一起变）---
/**
 * 主麦克风圆与 FAB「Tap to talk」圆共用直径（px）。
 * MicButton 用 px；FAB 用 rem（= 直径 ÷ ROOT_FONT_PX），避免同一数值写两遍不一致。
 */
export const FAB_MAIN_CIRCLE_DIAMETER_PX = 80;

/** 与 `FAB_MAIN_CIRCLE_DIAMETER_PX` 相同，保留别名便于在 Mic 相关代码里搜「WHISPER_MIC」 */
export const WHISPER_MIC_BUTTON_DIAMETER_PX = FAB_MAIN_CIRCLE_DIAMETER_PX;

/**
 * 主圆**内**图标边长（px）：Whisper 页 Mic / Loader / 停止方块，以及 FAB「Tap to talk」内 Mic。
 * 与整钮直径 `FAB_MAIN_CIRCLE_DIAMETER_PX`（外圈）独立，可单独调图标占比。
 */
export const FAB_MAIN_CIRCLE_ICON_PX = 42;

/** 与 `FAB_MAIN_CIRCLE_ICON_PX` 相同，便于在 Whisper 组件里按名称搜索 */
export const WHISPER_MIC_ICON_SIZE_PX = FAB_MAIN_CIRCLE_ICON_PX;

/** 主圆内实心麦克风 SVG path（Material Mic filled），与线框 Lucide Mic 区分 */
export const SOLID_MIC_SVG_PATH_D =
  'M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z';

/** FAB「Tap to talk」边长（rem），由主圆直径推导，与 Whisper 主麦视觉上一致 */
export const FAB_TAP_TO_TALK_BUTTON_SIZE_REM =
  FAB_MAIN_CIRCLE_DIAMETER_PX / ROOT_FONT_PX;

/** Check-in / Type chat 圆钮边长（rem）；可与主钮不同以区分层级 */
export const FAB_SIDE_BUTTONS_SIZE_REM = 4.5;

/** 外层 `fixed` 容器距视口底边（rem），与主麦竖直位置一起调 */
export const WHISPER_MIC_WRAPPER_BOTTOM_REM = 12;

/** 主麦圆心距视口底边（px），用于与 FAB 对齐 */
export const WHISPER_MIC_CENTER_FROM_VIEWPORT_BOTTOM_PX =
  WHISPER_MIC_WRAPPER_BOTTOM_REM * ROOT_FONT_PX + WHISPER_MIC_BUTTON_DIAMETER_PX / 2;

/**
 * 与 Layout 中「Tap to talk」文案 + gap-1.5 一致；改字号/行高时请同步。
 * 文案为 `text-[11px] leading-tight`，此处用一行近似高度（px）。
 */
export const FAB_TAP_TO_TALK_LABEL_HEIGHT_PX = 14;
/** 对应 `gap-1.5` → 0.375rem */
export const FAB_TAP_TO_TALK_LABEL_GAP_PX = 0.375 * ROOT_FONT_PX;

/**
 * 展开面板内「Tap to talk」列（文案 + 按钮）的 `top`（rem），由几何推导，使按钮圆心与
 * `WHISPER_MIC_CENTER_FROM_VIEWPORT_BOTTOM_PX` 对齐。
 */
export const FAB_MENU_TOP_BUTTON_TOP_OFFSET_REM = (() => {
  const panelBottomFromViewportBottomPx =
    TAB_BAR_H_PX + FAB_MENU_PANEL_GAP_BELOW_REM * ROOT_FONT_PX;
  const panelTopFromViewportBottomPx =
    panelBottomFromViewportBottomPx + FAB_MENU_PANEL_HEIGHT_REM * ROOT_FONT_PX;
  const tapCenterFromPanelTopPx =
    panelTopFromViewportBottomPx - WHISPER_MIC_CENTER_FROM_VIEWPORT_BOTTOM_PX;
  const buttonHalfPx = (FAB_TAP_TO_TALK_BUTTON_SIZE_REM * ROOT_FONT_PX) / 2;
  const topOffsetPx =
    tapCenterFromPanelTopPx -
    FAB_TAP_TO_TALK_LABEL_HEIGHT_PX -
    FAB_TAP_TO_TALK_LABEL_GAP_PX -
    buttonHalfPx;
  return topOffsetPx / ROOT_FONT_PX;
})();

// =============================================================================
// ⑤ FAB 三钮 — 毛玻璃光影
//
// 所有 "_BG_" 常量为 CSS gradient 字符串，用于 `background`（非 `backgroundColor`），
// 避免渐变失效。边框效果编码进 shadow 第一层（0 0 0 1px …），不单独设 border。
//
// 光影层次（4 层 box-shadow）：
//   1. 轮廓边：0 0 0 1px 白色半透明 → 玻璃边缘
//   2. 内顶高光：inset 顶部白色 → 模拟光从左上折射进玻璃
//   3. 内底阴影：inset 底部深色 → 增加玻璃厚度/体积感
//   4. 外投影/发光：blur 向外扩散 → 悬浮感
// =============================================================================

// --- Tap to talk（中心主钮）---
//
// 效果目标：细亮边，类似「border 有一段弧被遮住」——仅顶部一条薄亮线，不做大光斑。
//
// background：仅基础渐变 + 极小右下副光（右下角微弱，不做顶部白块）。
// box-shadow 四层：
//   1. 外轮廓边：0 0 0 1px 白色          → 完整一圈细玻璃边
//   2. 内顶亮线：inset 0 1.5px 0px       → blur=0，极窄，只亮顶部一条弧
//   3. 内底淡阴：inset 0 -1.5px 4px      → 给底部增加一点深度感
//   4. 外投影：blur 收小（12px）          → 清晰悬浮感，不漫散

export const FAB_TAP_TO_TALK_BG_LIGHT = [
  'radial-gradient(circle at 75% 78%, rgba(180,215,255,0.20) 0%, transparent 36%)',
  'linear-gradient(172deg, rgba(90,148,252,0.72) 0%, rgba(42,95,200,0.86) 100%)',
].join(', ');
export const FAB_TAP_TO_TALK_BG_DARK = [
  'radial-gradient(circle at 75% 78%, rgba(185,218,255,0.18) 0%, transparent 36%)',
  'linear-gradient(172deg, rgba(86,142,255,0.80) 0%, rgba(28,92,245,0.96) 100%)',
].join(', ');

export const FAB_TAP_TO_TALK_SHADOW = [
  'inset 0 1px 0.2px rgba(255,255,255,0.80)',  // 顶部细亮弧（无 blur，锐利）
  'inset 0 -1px 0.2px rgba(255, 255, 255, 0.8)',     // 底部细亮弧（无 blur，锐利）
  '0 4px 12px rgba(22,68,200,0.52)',           // 外投影，收紧清晰
].join(', ');

/** backdrop-filter blur px */
export const FAB_TAP_TO_TALK_BACKDROP_BLUR_PX = 12;

// --- Check-in / Type chat（左右侧钮，同逻辑，整体略浅区分主次）---

export const FAB_SIDE_BUTTONS_BG_LIGHT = [
  'radial-gradient(circle at 75% 78%, rgba(180,215,255,0.16) 0%, transparent 34%)',
  'linear-gradient(172deg, rgba(130,178,255,0.58) 0%, rgba(72,118,200,0.74) 100%)',
].join(', ');
export const FAB_SIDE_BUTTONS_BG_DARK = [
  'radial-gradient(circle at 75% 78%, rgba(185, 218, 255, 0) 0%, transparent 34%)',
  'linear-gradient(172deg, rgba(145, 191, 255, 0.06) 0%, rgba(98, 153, 255, 0.08) 100%)',
].join(', ');

export const FAB_SIDE_BUTTONS_SHADOW = [
  'inset 0 1px 0px rgba(255, 255, 255, 0.3)',
  'inset 0 -1px 0px rgba(255, 255, 255, 0.3)',
  '0 4px 10px rgba(22, 65, 185, 0.32)',
].join(', ');

export const FAB_SIDE_BUTTONS_BACKDROP_BLUR_PX = 12;

// =============================================================================
// ⑥ Whisper 主麦克风 — 视觉（MicButton）
// =============================================================================

export const WHISPER_MIC_Z_INDEX = 1000;

export const WHISPER_MIC_BG_DARK = 'rgba(255, 255, 255, 0.15)';
export const WHISPER_MIC_BG_LIGHT = 'rgba(255, 255, 255, 0.6)';
export const WHISPER_MIC_BACKDROP_FILTER = 'blur(20px) saturate(180%)';
export const WHISPER_MIC_BORDER_DARK = '1px solid rgba(255, 255, 255, 0.3)';
export const WHISPER_MIC_BORDER_LIGHT = '1px solid rgba(255, 255, 255, 0.7)';
export const WHISPER_MIC_SHADOW_DARK =
  '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4)';
export const WHISPER_MIC_SHADOW_LIGHT =
  '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)';

/** 首次引导文案与按钮间距（原 `mb-6` = 1.5rem） */
export const WHISPER_MIC_FIRST_HINT_MARGIN_BOTTOM_REM = 1.5;
